<script lang="ts" setup>
import { Typo, Icon, Spinner } from '@travelata-libs/ui-kit';
import { parseDate, formatDateWithTimezone, getCurrentTimeZone, addDaysToDate, pluralize } from '@travelata-libs/utils';
import { AccountUser } from '@/features/header-account';
import { SiteHeader } from '@/widgets/site-header';
import { ApiTour } from '@/entities/tour';
import { computed } from 'vue';

    interface HeaderProps {
        user?: AccountUser | null;
        accountUrl?: string;
        tour: ApiTour | null;
    }
 
    interface HeaderEmits {
        (e: 'login'): void;
        (e: 'logout'): void;
    }

    const emit = defineEmits<HeaderEmits>();

    const props = defineProps<HeaderProps>();

    const formatDate = (date: Date) => {
        return formatDateWithTimezone(date, getCurrentTimeZone(), 'd MMMM');
    }

    const loading = computed(() => !props.tour);

    const touristsText = computed(() => {
        const touristsGroup = props.tour?.touristGroup;
        if (!touristsGroup) return '';
        const roomsCount = props.tour?.roomGroups.length || 1;
        const touristsCount = touristsGroup?.adults + touristsGroup?.infants + touristsGroup?.kids;
        return `${pluralize(['номер', 'номера', 'номеров'], roomsCount)} для ${pluralize(['туриста', 'туристов', 'туристов'], touristsCount)}`;
    });

    const checkInDateText = computed(() => {
        const dateFrom = parseDate(props.tour?.checkInDate || '');
        if (!dateFrom) return '';
        const dateTo = addDaysToDate(dateFrom, props.tour?.nights.tour || 0);
        return `${formatDate(dateFrom)} — ${formatDate(dateTo)}`;
    });

</script>

<template>
    <header class="tour-page-header">
        <SiteHeader
            :user="user"
            :account-url="accountUrl"
            :buy-visible="false"
            @login="emit('login')"
            @logout="emit('logout')"
            class="tour-page-header__site"
        >
            <template #nav>
                <div class="tour-page-header__container">
                    <div v-if="loading" class="tour-page-header__loader">
                        <Spinner size="xs" color="white" />
                        <Typo> Загружаем информацию о туре</Typo>
                    </div>
                    <template v-else>
                        <Typo> {{ tour.departureCity.name }} </Typo>
                        <Icon class="tour-page-header__icon" name="chevron-right" size="m" />
                        <Typo> {{ tour.country.name}}, {{ tour.resort.name }} </Typo>
                        <Icon class="tour-page-header__icon" name="point-filled" size="xxs" />
                        <Typo> {{ checkInDateText }} </Typo>
                        <Icon class="tour-page-header__icon" name="point-filled" size="xxs" />
                        <Typo> {{ touristsText }} </Typo>
                    </template>
                </div>
            </template>
        </SiteHeader>
    </header>
</template>

<style lang="scss" scoped>
    .tour-page-header {
        color: var(--color-text-inverse);
        p {
            text-box: trim-both cap alphabetic;
        }
        &__site {
            padding-bottom: var(--spacing-s);
        }
        &__loader {
            display: flex;
            align-items: center;
            gap: var(--spacing-s);
        }
        &__container {
            display: flex;
            align-items: center;
            gap: var(--spacing-xxs);
        }
        &__icon {
            fill: var(--color-text-inverse);
        }
    }

</style>